import React, { useContext, useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Box, Typography, CircularProgress, Button, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useObserver } from "mobx-react-lite";

// Core
import { Stores, StoresContext } from "Custom/Stores";

// Custom
import { RouteHelper } from "Custom/Utils";
import { ViewReservationForm } from "Custom/Views/DashboardSubViews/ViewReservationForm";
import { Client } from "Custom/Globals/AppUrls";
import { SignatureRequestPageViewModel } from "Custom/ViewModels/SignatureRequestPageViewModel";
import { Error } from "Custom/Components/Error";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { Loader } from "Core/Components/Loader";
import { PageMenu } from "Custom/Views/PageMenu";
import { PageTitle } from "Custom/Components/PageTitle";

interface SignatureRequestPageProps {
    propertyId: string;
    reservationId: string;
    signatureToken: string;
}

const useStyles = makeStyles(theme => ({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
    },
    paper: {
        padding: theme.spacing(4),
        maxWidth: 600,
        width: "100%",
        textAlign: "center",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    errorContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginTop: theme.spacing(2),
    },
}));

export const SignatureRequestPage: React.FC = () => {
    const classes = useStyles();
    const { propertyId, reservationId, signatureToken } = useParams<SignatureRequestPageProps>();
    const location = useLocation();
    const history = useHistory();
    const store = useContext<Stores>(StoresContext);

    const [viewModel] = useState(() => new SignatureRequestPageViewModel());

    useEffect(() => {
        // Check if user is logged in first
        if (!store.domain.AccountStore.IsLoggedIn) {
            // Store the current URL for post-login redirect
            const currentUrl = location.pathname + location.search;
            localStorage.setItem("signatureRedirectUrl", currentUrl);

            // Redirect to login page if not logged in
            history.push(RouteHelper.ClientRoutes.login());
            return;
        }

        // The token validation will ensure they have access to this specific signature

        // Validate signature token (purchaserType will be determined from the token)
        viewModel.validateSignatureToken(reservationId, signatureToken);
    }, [propertyId, reservationId, signatureToken, store.domain.AccountStore.IsLoggedIn, history, location]);

    const handleBackToDashboard = () => {
        if (store.domain.AccountStore.IsLoggedIn && store.domain.AccountStore.PropertyId) {
            history.push(RouteHelper.ClientRoutes.Dashboard(store.domain.AccountStore.PropertyId));
        }
    };

    const renderBusy = () => (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Loader />
        </Box>
    );

    const renderError = () => (
        <Box display="flex" flexDirection="column" alignItems="center" minHeight="400px" padding={3}>
            <Error errorText={viewModel.validationError || "This signature request is invalid or has expired."} />
            <Button variant="contained" color="primary" onClick={handleBackToDashboard} style={{ marginTop: 16 }}>
                Back to Dashboard
            </Button>
        </Box>
    );

    const renderSuccess = () => {
        if (viewModel.isValidToken && viewModel.propertyId && viewModel.purchaserType) {
            // Check if signature is already completed
            if (viewModel.isSignatureComplete) {
                return (
                    <Box display="flex" flexDirection="column" alignItems="center" minHeight="400px" padding={3}>
                        <Paper className={classes.paper}>
                            <Typography variant="h5" gutterBottom style={{ color: "#4caf50", fontWeight: "bold" }}>
                                ✓ Signature Already Completed
                            </Typography>
                            <Typography variant="body1" style={{ marginBottom: "16px" }}>
                                This signature request has already been completed. You do not need to sign again.
                            </Typography>
                            <Typography variant="body2" style={{ marginBottom: "24px", color: "#666" }}>
                                If you have any questions about this signature, please contact your sales agent.
                            </Typography>
                            <Button variant="contained" color="primary" onClick={handleBackToDashboard}>
                                Back to Dashboard
                            </Button>
                        </Paper>
                    </Box>
                );
            }

            // Show the signature form if not completed
            return (
                <ViewReservationForm
                    propertyId={viewModel.propertyId}
                    reservationId={reservationId}
                    signatureToken={signatureToken}
                    purchaserType={viewModel.purchaserType}
                    isSignatureRequest={true}
                />
            );
        }
        return null;
    };

    return useObserver(() => {
        // Show loading while checking authentication
        if (!store.domain.AccountStore.IsLoggedIn) {
            return (
                <Box className={classes.container}>
                    <Paper className={classes.paper}>
                        <Box className={classes.errorContainer}>
                            <Typography variant="h6" gutterBottom>
                                Redirecting to login...
                            </Typography>
                            <CircularProgress />
                        </Box>
                    </Paper>
                </Box>
            );
        }

        return (
            <React.Fragment>
                <PageMenu />
                <PageTitle title="Reservation Form" />
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.success && <React.Fragment>{renderSuccess()}</React.Fragment>}
            </React.Fragment>
        );
    });
};
